import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Sparkles, Loader2, CalendarDays, Mic, Square, Volume2, VolumeX } from "lucide-react";
import { conciergeInfo, conciergeChat, conciergeBook, conciergeVoice, conciergeSpeak } from "@/lib/api";

/**
 * Unified floating AI concierge, themed per vertical.
 * Props: demo (theme id), concierge { name, role, badge, accent, bg, text }.
 */
const PANEL_TID = { restaurant: "aria", "real-estate": "haven", medical: "clara", law: "intake", spa: "willow", physio: "avery", fashion: "elle", charity: "hope", portfolio: "vale", hotel: "sola", salon: "coco", grocery: "basil", auto: "max", college: "grove", saas: "nova" };
const TRIGGER_TID = { restaurant: "aurelia", "real-estate": "maya", medical: "medical", law: "law", spa: "stillwater", physio: "physio", fashion: "elara", charity: "harvest", portfolio: "amara", hotel: "solara", salon: "lume", grocery: "fresh", auto: "precision", college: "grove", saas: "nova" };
const BOOK_LABEL = { restaurant: "Reserve a table", "real-estate": "Book a tour", medical: "Request appointment", law: "Request consultation", spa: "Book a massage", physio: "Request appointment", fashion: "Book styling", charity: "Get involved", portfolio: "Book a call", hotel: "Book a stay", salon: "Book appointment", grocery: "Order groceries", auto: "Book service", college: "Schedule a visit", saas: "Talk to sales" };

export default function AIConciergeWidget({ demo, concierge }) {
  const { name, role, badge, accent, bg, text } = concierge;
  const panelTid = PANEL_TID[demo] || "haven";
  const triggerTid = TRIGGER_TID[demo] || "maya";
  const bookLabel = BOOK_LABEL[demo] || "Request appointment";
  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [greeting, setGreeting] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [bookBusy, setBookBusy] = useState(false);
  const [bookErr, setBookErr] = useState("");
  const [form, setForm] = useState({ name: "", contact: "", date: "", time: "", notes: "" });
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const sessionRef = useRef(`c-${demo}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    conciergeInfo(demo).then((d) => {
      setPrompts(d.prompts || []);
      setGreeting(d.greeting || `Hi, I'm ${name}.`);
    }).catch(() => {});
  }, [demo, name]);

  useEffect(() => {
    if (open && messages.length === 0 && greeting) {
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [open, greeting, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Allow any page CTA to open the concierge (optionally straight to the booking form).
  useEffect(() => {
    const openChat = () => setOpen(true);
    const openBook = () => { setOpen(true); setShowBook(true); };
    window.addEventListener("concierge:open", openChat);
    window.addEventListener("concierge:book", openBook);
    // Auto-open when a visitor arrives via a "?ai=1" (chat) or "?ai=book" (booking) link.
    try {
      const ai = new URLSearchParams(window.location.search).get("ai");
      if (ai === "book") { setOpen(true); setShowBook(true); }
      else if (ai) { setOpen(true); }
    } catch { /* noop */ }
    return () => { window.removeEventListener("concierge:open", openChat); window.removeEventListener("concierge:book", openBook); };
  }, []);


  const speak = async (textToSpeak) => {
    try {
      const { audio_base64, mime } = await conciergeSpeak(textToSpeak, demo);
      if (audioRef.current) { audioRef.current.pause(); }
      const el = new Audio(`data:${mime};base64,${audio_base64}`);
      audioRef.current = el;
      el.play().catch(() => {});
    } catch { /* silent — spoken reply is best-effort */ }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const history = messages.map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await conciergeChat(demo, q, history, sessionRef.current);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      if (voiceOn) speak(res.reply);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had trouble responding. Please try again." }]);
    } finally { setBusy(false); }
  };

  const startRecording = async () => {
    if (recording || busy || voiceBusy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 800) { setVoiceBusy(false); return; }
        setVoiceBusy(true);
        const history = messages.map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));
        const fd = new FormData();
        fd.append("demo", demo);
        fd.append("session_id", sessionRef.current);
        fd.append("history", JSON.stringify(history));
        fd.append("audio", blob, "clip.webm");
        try {
          const res = await conciergeVoice(fd);
          const transcript = (res.transcript || "").trim();
          if (transcript.length < 2) {
            setMessages((m) => [...m, { role: "assistant", content: "I couldn't quite catch that — please try again or type your message." }]);
          } else {
            setMessages((m) => [...m, { role: "user", content: transcript }, { role: "assistant", content: res.reply }]);
            if (voiceOn) speak(res.reply);
          }
        } catch {
          setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't catch that. Please try again or type your message." }]);
        } finally { setVoiceBusy(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't access your microphone. Please allow mic access or type your message." }]);
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) { recorderRef.current.stop(); setRecording(false); }
  };

  const submitBooking = async () => {
    setBookErr("");
    const nm = form.name.trim();
    const contact = form.contact.trim();
    if (!nm || !contact || !form.date || !form.time) {
      setBookErr("Please fill in your name, contact, date and time.");
      return;
    }
    const isEmail = contact.includes("@");
    setBookBusy(true);
    try {
      const res = await conciergeBook({
        demo, session_id: sessionRef.current, name: nm,
        email: isEmail ? contact : null, phone: isEmail ? null : contact,
        date: form.date, time: form.time, notes: form.notes.trim() || null,
      });
      setShowBook(false);
      setForm({ name: "", contact: "", date: "", time: "", notes: "" });
      if (!open) setOpen(true);
      setMessages((m) => [...(m.length ? m : [{ role: "assistant", content: greeting }]),
        { role: "assistant", content: res.confirmation }]);
    } catch (e) {
      setBookErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBookBusy(false); }
  };

  const inputStyle = { color: text, background: `${accent}12`, border: `1px solid ${accent}33` };

  return (
    <div className="fixed bottom-5 right-5 z-[60]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`${panelTid}-chat-panel`}
            className="mb-3 w-[92vw] max-w-sm overflow-hidden rounded-2xl shadow-2xl border max-h-[78vh] flex flex-col"
            style={{ background: bg, color: text, borderColor: `${accent}33` }}
          >
            {/* header */}
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${accent}22` }}>
              <div className="flex items-center gap-2.5">
                <div className="grid h-9 w-9 place-items-center rounded-full font-bold" style={{ background: accent, color: bg }}>{badge}</div>
                <div>
                  <div className="text-sm font-semibold leading-tight">{name}</div>
                  <div className="text-[11px] opacity-60 leading-tight">{role}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setVoiceOn((v) => !v)} data-testid={`${panelTid}-voice-toggle`}
                  title={voiceOn ? "Spoken replies on" : "Spoken replies off"}
                  className="grid h-7 w-7 place-items-center rounded-full transition-opacity hover:opacity-90"
                  style={{ background: voiceOn ? accent : `${accent}22`, color: voiceOn ? bg : text }}>
                  {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <button onClick={() => setShowBook((b) => !b)} data-testid={`${panelTid}-book-btn`}
                  title={bookLabel}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-opacity hover:opacity-90"
                  style={{ background: accent, color: bg }}>
                  <CalendarDays size={13} /> Book
                </button>
                <button onClick={() => setOpen(false)} data-testid="concierge-close" className="ml-1 opacity-60 hover:opacity-100 transition-opacity"><X size={18} /></button>
              </div>
            </div>

            {showBook ? (
              <div data-testid={`${panelTid}-book-form`} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5">
                <div className="text-sm font-semibold">{bookLabel}</div>
                <div className="text-[11px] opacity-60 -mt-1">Share your details and the team will confirm.</div>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name" data-testid={`${panelTid}-book-name`}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none placeholder:opacity-50" style={inputStyle} />
                <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="Email or phone" data-testid={`${panelTid}-book-contact`}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none placeholder:opacity-50" style={inputStyle} />
                <div className="flex gap-2">
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid={`${panelTid}-book-date`}
                    className="w-1/2 rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle} />
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid={`${panelTid}-book-time`}
                    className="w-1/2 rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle} />
                </div>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything we should know? (optional)" rows={2} data-testid={`${panelTid}-book-notes`}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none placeholder:opacity-50 resize-none" style={inputStyle} />
                {bookErr && <div className="text-[11px] text-red-400" data-testid={`${panelTid}-book-error`}>{bookErr}</div>}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setShowBook(false)} className="flex-1 rounded-lg px-3 py-2 text-sm font-medium"
                    style={{ border: `1px solid ${accent}33`, color: text }}>Cancel</button>
                  <button onClick={submitBooking} disabled={bookBusy} data-testid={`${panelTid}-book-submit`}
                    className="flex-1 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ background: accent, color: bg }}>
                    {bookBusy ? <Loader2 size={14} className="animate-spin" /> : null} Send request
                  </button>
                </div>
              </div>
            ) : (
            <>
            {/* messages */}
            <div ref={scrollRef} className="h-[min(52vh,320px)] overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed"
                    style={m.role === "user"
                      ? { background: accent, color: bg }
                      : { background: `${accent}14`, color: text }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-3.5 py-2 text-sm" style={{ background: `${accent}14` }}>
                    <Loader2 size={15} className="animate-spin" style={{ color: accent }} />
                  </div>
                </div>
              )}
              {messages.length <= 1 && prompts.length > 0 && (
                <div className="pt-1 space-y-2">
                  {prompts.map((p, pi) => (
                    <button key={p} onClick={() => send(p)} data-testid={`concierge-prompt-${pi}`} className="block w-full text-left text-xs rounded-lg px-3 py-2 transition-colors"
                      style={{ border: `1px solid ${accent}33`, color: text }}>
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* input */}
            <div className="p-3" style={{ borderTop: `1px solid ${accent}22` }}>
              <div className="flex items-center gap-2 rounded-full px-3 py-1.5" style={{ background: `${accent}12` }}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={`Ask ${name} anything…`}
                  data-testid={`${panelTid}-chat-input`}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:opacity-50"
                  style={{ color: text }}
                />
                <button onClick={recording ? stopRecording : startRecording} disabled={busy || voiceBusy}
                  data-testid={`${panelTid}-mic-btn`} title={recording ? "Stop recording" : "Tap to talk"}
                  className="grid h-8 w-8 place-items-center rounded-full disabled:opacity-50 transition-colors"
                  style={recording ? { background: "#e11d48", color: "#fff" } : { background: `${accent}22`, color: text }}>
                  {voiceBusy ? <Loader2 size={15} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={15} />}
                </button>
                <button onClick={() => send()} disabled={busy} data-testid={`${panelTid}-send-btn`}
                  className="grid h-8 w-8 place-items-center rounded-full disabled:opacity-50" style={{ background: accent, color: bg }}>
                  <Send size={15} />
                </button>
              </div>
              <div className="mt-2 text-center text-[10px] opacity-45">
                {recording ? "Listening… tap the square to send" : voiceBusy ? "Transcribing…" : "AI assistant by WebsiteTech.pro · tap the mic to talk"}
              </div>
            </div>
            </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${triggerTid}-concierge-trigger`}
        className="ml-auto flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 shadow-xl"
        style={{ background: bg, color: text, border: `1px solid ${accent}44` }}
      >
        <span className="grid h-8 w-8 place-items-center rounded-full" style={{ background: accent, color: bg }}>
          <Sparkles size={16} />
        </span>
        <span className="text-sm font-semibold">Meet {name}</span>
      </motion.button>
    </div>
  );
}
